import React, { memo, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import {
  Alert,
  Box,
  Button,
  Skeleton,
  Stack,
  Card,
  Typography,
  Chip,
  Divider,
  CardHeader,
  Tooltip,
  Icon,
  CardContent,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  useMediaQuery
} from 'investira.react.components';
import { displays } from 'investira.react.lib';
import { formats } from 'investira.sdk';
import { CorrelacaoMatriz, CorrelacaoHeatMap } from '../../../components/organisms';
import { MelhorValorTableCell, PessoaImage } from '../../../components/molecules';
import services from '../../../services';
import utils from '../../../utils';

const wTaxas = {
  adm: {
    criterion: 'lower',
    getValue: pAtivo => pAtivo?.fundo?.taxas?.adm
  },
  performance: {
    criterion: 'lower',
    getValue: pAtivo => pAtivo?.fundo?.performance?.taxa
  }
};

const ComparadorTaxasLiquidez = memo(({ ativos = [], highlightEnabled = false }) => {
  const isUpSm = useMediaQuery(pTheme => pTheme.breakpoints.up('sm'));

  const xBestIndexes = Object.entries(wTaxas).reduce(
    (pBestIndexes, [pKey, pTaxa]) => ({
      ...pBestIndexes,
      [pKey]: utils.comparisons.getBestValueIndexes(ativos.map(pTaxa.getValue), pTaxa.criterion)
    }),
    {}
  );

  const pvTaxa = pValue =>
    typeof pValue === 'number' && Number.isFinite(pValue) ? (
      <Typography variant="caption">{`${formats.formatNumber(pValue, 2)}%`}</Typography>
    ) : (
      <Tooltip title="Sem dado" arrow>
        <Box component="span" tabIndex={0} sx={{ display: 'inline-flex' }}>
          <Typography component="span" variant="caption" color="textSecondary" fontWeight="bold">
            -
          </Typography>
        </Box>
      </Tooltip>
    );

  return (
    <Stack
      flexGrow={1}
      data-testid="comparador-taxas-liquidez"
      data-highlight-enabled={String(highlightEnabled)}>
      <Card component="section">
        <CardHeader
          title={
            <Stack direction="row" gap={1} alignItems="center">
              <Typography
                variant="subtitle1"
                component="h3"
                color="textPrimary"
                sx={{ fontWeight: 600 }}
                noWrap>
                {/* Taxas e Liquidez */}
                Taxas
              </Typography>
              <Tooltip
                title={
                  <Stack gap={1} p={1}>
                    <Typography variant="caption">
                      <b>Taxa de administração:</b> percentual cobrado pelo fundo para remunerar sua
                      gestão e operação.
                    </Typography>
                    <Typography variant="caption">
                      <b>Taxa de performance:</b> cobrança adicional quando o fundo supera o índice
                      de referência definido.
                    </Typography>
                    {/* <Typography variant="caption">
                      <b>Liquidez:</b> prazo necessário para converter o investimento resgatado em
                      dinheiro disponível.
                    </Typography> */}
                  </Stack>
                }
                arrow>
                <span>
                  <Icon iconName="information" size={16} color="primary" />
                </span>
              </Tooltip>
            </Stack>
          }
        />
        <CardContent>
          <Stack sx={{ pt: 0 }}>
            <TableContainer id="table-container" sx={{ overflow: 'auto' }}>
              <Table sx={{ minWidth: 650 }} size="small" tableLayout="fixed">
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        position: 'sticky',
                        left: 0,
                        zIndex: 1,
                        backgroundColor: pTheme => pTheme.palette.secondary.dark
                      }}
                    />
                    <TableCell align="right">Taxa de Administração</TableCell>
                    <TableCell align="right">Taxa de Perfomance</TableCell>
                    {/* <TableCell align="right">Liquidez</TableCell> */}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ativos.map((pAtivo, pIndex) => {
                    const xRowTestId = pAtivo.ativo_seuid.replace(/[A-Z]/g, pValue => {
                      return `-${pValue.toLowerCase()}`;
                    });

                    const xAtivoDescricao = utils.helpers.getAtivoDescricao(pAtivo);
                    const xAtivoLabel = isUpSm
                      ? xAtivoDescricao
                      : utils.typo.shortname(xAtivoDescricao, 25);

                    return (
                      <TableRow key={xRowTestId}>
                        <TableCell
                          component="th"
                          scope="row"
                          sx={{
                            position: 'sticky',
                            left: 0,
                            zIndex: 1,
                            backgroundColor: pTheme => pTheme.palette.secondary.dark,
                            maxWidth: !isUpSm ? '200px' : 'none'
                          }}>
                          <Stack
                            direction="row"
                            gap={1}
                            alignItems="center"
                            sx={{ '& img': { display: 'block' } }}>
                            {pAtivo?.logo_pessoa_id && (
                              <PessoaImage
                                logoPessoaId={pAtivo.logo_pessoa_id}
                                alt={pAtivo.pessoa}
                                hasLogo={pAtivo.logo_ok}
                                size={18}
                              />
                            )}
                            <Stack>
                              <Typography
                                data-testid={`rentabilidade-resumo-[ativo]-value`}
                                variant="body2"
                                color="textPrimary"
                                sx={{ fontWeight: 500 }}
                                noWrap>
                                {xAtivoLabel}
                              </Typography>
                            </Stack>
                          </Stack>
                        </TableCell>
                        <MelhorValorTableCell
                          align="right"
                          isBest={highlightEnabled && xBestIndexes.adm.includes(pIndex)}>
                          {pvTaxa(pAtivo?.fundo?.taxas?.adm)}
                        </MelhorValorTableCell>
                        <MelhorValorTableCell
                          align="right"
                          isBest={highlightEnabled && xBestIndexes.performance.includes(pIndex)}>
                          {pvTaxa(pAtivo?.fundo?.performance?.taxa)}
                        </MelhorValorTableCell>
                        {/* <TableCell align="right">
                          [Liquidez]
                        </TableCell> */}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );

  return (
    <Stack
      direction="row"
      gap={1}
      data-comparador-header-scroll="horizontal"
      sx={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>
      {ativos.map((xFundo, xIdx) => {
        return (
          <Typography
            variant="subtitle1"
            component="h2"
            color="textPrimary"
            sx={{ fontWeight: 600 }}>
            {utils.helpers.getAtivoDescricao(xFundo)}
          </Typography>
        );
      })}
    </Stack>
  );
});

export default ComparadorTaxasLiquidez;
